import { emptyIfInstant } from "./mockMode";

/**
 * Mock data for the catalog + BOM module on the admin app.
 *
 * Mirrors the shapes defined in `instant.schema.ts`.
 */

const NOW = new Date("2026-05-09T08:00:00Z").getTime();
const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

export type ProductKind = "manufactured" | "imported" | "service";

export type MockBrand = {
  _id: string;
  code: string;
  displayName: string;
  description: string | null;
  isActive: boolean;
};

export type MockCategory = {
  _id: string;
  code: string;
  displayName: string;
  parentCategoryId: string | null;
  sortOrder: number;
  isActive: boolean;
};

export type MockUnit = {
  _id: string;
  code: string;
  displayName: string;
  dimension: string;
  conversionFactor: number;
  isActive: boolean;
};

export type MockTaxRate = {
  _id: string;
  code: string;
  displayName: string;
  ratePercentE4: bigint;
  taxType: string;
  isActive: boolean;
  isDefault: boolean;
};

export type MockProduct = {
  _id: string;
  sku: string;
  displayName: string;
  description: string | null;
  productKind: ProductKind;
  brandId: string | null;
  categoryId: string | null;
  primaryUnitId: string;
  defaultTaxRateId: string | null;
  hsCode: string | null;
  reorderQuantity: number | null;
  barcodePrimary: string | null;
  /** Approximate standard cost in BDT cents (UI-only, not in schema yet). */
  standardCostCents: bigint;
  isActive: boolean;
  createdAt: number;
  updatedAt: number;
};

export type MockProductVariant = {
  _id: string;
  productId: string;
  variantSku: string;
  variantName: string;
  attributesJson: string;
  barcode: string | null;
  unitId: string | null;
  weightGrams: number | null;
  isActive: boolean;
  updatedAt: number;
};

export type MockBom = {
  _id: string;
  productId: string;
  productVariantId: string | null;
  code: string;
  versionLabel: string;
  isActive: boolean;
  outputQuantity: number;
  outputUnitId: string;
  notes: string | null;
  activatedAt: number | null;
  createdAt: number;
  updatedAt: number;
};

export type MockBomLine = {
  _id: string;
  bomId: string;
  componentProductId: string;
  componentVariantId: string | null;
  quantity: number;
  unitId: string;
  /** Wastage percent * 1e4 (e.g. 2.50% = 25000). */
  wastageAllowanceE4: bigint;
  notes: string | null;
  sortOrder: number;
};

/* ──────────── Seed ──────────── */

export const mockBrands: MockBrand[] = emptyIfInstant<MockBrand>([]);

export const mockCategories: MockCategory[] = emptyIfInstant<MockCategory>([]);

export const mockUnits: MockUnit[] = emptyIfInstant<MockUnit>([]);

export const mockTaxRates: MockTaxRate[] = emptyIfInstant<MockTaxRate>([]);

export const mockProducts: MockProduct[] = emptyIfInstant<MockProduct>([]);

export const mockProductVariants: MockProductVariant[] = emptyIfInstant<MockProductVariant>([]);

export const mockBoms: MockBom[] = emptyIfInstant<MockBom>([]);

export const mockBomLines: MockBomLine[] = emptyIfInstant<MockBomLine>([]);
